import type { Bauordnung, BauordnungBundle, OrgModell, Regel } from './types';
import type { Baustellenordnung, Handbuch, OrgModell as HandbuchOrgModell } from '../handbuch/types';

/**
 * Die EINMALIGE Uebernahme aus dem SiGe-Handbuch (Spezifikation D, E2).
 *
 * Das Handbuch fuehrt in `handbuecher.ordnung` fuenf Felder einer
 * Baustellenordnung: Arbeitszeiten, Zugang, Regeln, Notfallschritte,
 * Sammelplatz. Wer ein Projekt mit Handbuch anlegt, soll das nicht noch einmal
 * tippen — aber auch nicht dauerhaft gespiegelt bekommen: ab der Uebernahme
 * werden beide Dokumente GETRENNT gefuehrt. Der Kopfsatz haelt den Zeitpunkt
 * fest (`uebernommen_handbuch_am`), und `uebernahmeAusHandbuch` liefert danach
 * `null`.
 *
 * ═══ ES WIRD NICHTS BESTAETIGT ════════════════════════════════════════════
 * Jede uebernommene Zeile entsteht als Regel mit `quelle: 'handbuch'` und OHNE
 * `bestaetigt_am`. Sie ist damit ein Vorschlag im Sinne des Moduls (eine Zeile
 * ohne Bestaetigung steht nicht im Dokument) und wird im Kapitel-Reiter
 * einzeln bestaetigt — die Startwerte aus dem Handbuch sind „m (Bestaetigung)",
 * nicht „d" (Spezifikation D).
 *
 * ═══ NICHTS WIRD UEBERSCHRIEBEN ═══════════════════════════════════════════
 * Die Funktion fuegt nur hinzu. Ein Wortlaut, der im Zielkapitel schon steht
 * (gleich ob bestaetigt oder nicht), wird uebersprungen und benannt. Das
 * Handbuch selbst wird nicht angefasst — zurueck kommt ein Entwurf, geschrieben
 * wird im Dienst (`uebernehmeAusHandbuch`).
 *
 * ═══ ORGANISATIONSMODELL ══════════════════════════════════════════════════
 * Beide Kopfsaetze tragen `org_modell`, aber mit verschiedener Wertemenge: die
 * Bauordnung kennt zusaetzlich `'arge'` (bauordnung.sql), das Handbuch nicht
 * (handbuch_koordinierung.sql). Die vier gemeinsamen Werte gehen 1:1 ueber;
 * `'arge'` kann aus dem Handbuch nie entstehen, und ein am Kopfsatz bereits
 * gesetztes Modell — auch `'arge'` — bleibt stehen.
 *
 * Rein — kein DOM, keine Supabase.
 */

/** Die fuenf Felder von `handbuecher.ordnung`. */
export type HandbuchFeld = keyof Baustellenordnung;

export const HANDBUCH_FELDER: readonly HandbuchFeld[] =
    ['arbeitszeiten', 'zugang', 'regeln', 'notfallschritte', 'sammelplatz'];

/**
 * In welches Kapitel der BAUSTELLENORDNUNG jedes Handbuchfeld geht.
 *
 * Die Kapitel `arbeitszeiten`, `zugang` und `sammelplaetze` gibt es im Katalog,
 * aber nur der HSE-Plan druckt sie (`DOKUMENTE.hse`). Die Uebernahme zielt auf
 * die Baustellenordnung — deshalb landen die Felder in deren Kapiteln, sonst
 * stuenden die Startwerte in einem Dokument, das das Projekt vielleicht gar
 * nicht erzeugt. Der Test haelt fest, dass jedes Ziel in
 * `DOKUMENTE.ordnung.kapitel` vorkommt.
 */
export const HANDBUCH_FELD_KAPITEL: Readonly<Record<HandbuchFeld, string>> = {
    arbeitszeiten: 'allgemeine_informationen',
    zugang: 'allgemeine_regelungen',
    regeln: 'allgemeine_regelungen',
    notfallschritte: 'notfallmanagement',
    sammelplatz: 'notfallmanagement',
};

export const HANDBUCH_FELD_LABEL: Readonly<Record<HandbuchFeld, string>> = {
    arbeitszeiten: 'Arbeitszeiten',
    zugang: 'Zugang zur Baustelle',
    regeln: 'Regeln auf dieser Baustelle',
    notfallschritte: 'Verhalten im Notfall',
    sammelplatz: 'Sammelplatz',
};

/** Eine Regel, wie sie aus der Uebernahme entsteht — noch ohne Datenbank-Id. */
export interface HandbuchRegelEntwurf {
    kapitel: string;
    wortlaut: string;
    quelle: 'handbuch';
    art: 'regel';
    sortierung: number;
    /** Aus welchem Handbuchfeld die Zeile stammt — fuer Anzeige und Historie. */
    feld: HandbuchFeld;
}

export interface UebersprungeneZeile {
    feld: HandbuchFeld;
    wortlaut: string;
    grund: string;
}

export interface HandbuchUebernahme {
    regeln: HandbuchRegelEntwurf[];
    /** Nur gesetzt, wenn der Kopfsatz noch kein Modell traegt und das Handbuch eines hat. */
    org_modell: OrgModell | null;
    uebersprungen: UebersprungeneZeile[];
    /** Zeilen je Feld, die uebernommen werden — fuer die Vorschau vor dem Klick. */
    jeFeld: { feld: HandbuchFeld; anzahl: number }[];
}

export const istAusHandbuchUebernommen = (o: Pick<Bauordnung, 'uebernommen_handbuch_am'> | null): boolean =>
    !!o?.uebernommen_handbuch_am;

/**
 * Das gelesene Handbuch, wie der Container es dem Grundlagen-Reiter reicht.
 *
 * `wert: null` heisst „es gibt keins" — und das ist NUR dann eine Aussage,
 * wenn `fehler` leer ist. Ein Netz- oder Rechtefehler beim Lesen darf nicht
 * als „kein Handbuch, nichts zu uebernehmen" erscheinen: dann unterbliebe die
 * Uebernahme still, und niemand wuesste, dass es etwas zu holen gab. Mit
 * `fehler` zeigt der Kasten den Lesefehler, sperrt die Uebernahme und bietet
 * „Erneut laden".
 */
export interface HandbuchQuelle {
    wert: Handbuch | null;
    fehler: string | null;
}

/**
 * Das Modell, das die Uebernahme am Kopfsatz setzt — oder `null`.
 *
 * `null` heisst „nichts schreiben", nicht „auf null setzen": ein gesetztes
 * Modell am Kopfsatz wird nie ueberschrieben, und ein Handbuch ohne Modell
 * schlaegt keines vor.
 */
export const orgModellAusHandbuch = (
    handbuch: HandbuchOrgModell | null | undefined,
    kopfsatz: OrgModell | null | undefined,
): OrgModell | null => {
    if (kopfsatz) return null;
    if (!handbuch) return null;
    // Die Wertemenge des Handbuchs ist eine Teilmenge der Bauordnung — jeder
    // Handbuchwert ist ein gueltiger Bauordnungswert. `'arge'` kommt hier nie an.
    return handbuch;
};

/** Vergleichsschluessel eines Wortlauts — Gross-/Kleinschreibung und Rand egal. */
const schluessel = (wortlaut: string): string => wortlaut.trim().toLowerCase();

const satz = (praefix: string, text: string): string => `${praefix}: ${text.trim()}`;

/** Die Zeilen eines Handbuchfelds, so wie sie als Regel stehen sollen. */
const zeilenDesFelds = (ordnung: Partial<Baustellenordnung> | null | undefined, feld: HandbuchFeld): string[] => {
    const wert = ordnung?.[feld];
    if (Array.isArray(wert)) {
        return wert.map(z => String(z ?? '').trim()).filter(Boolean);
    }
    const text = String(wert ?? '').trim();
    if (!text) return [];
    switch (feld) {
        case 'arbeitszeiten': return [satz('Arbeitszeiten', text)];
        case 'zugang': return [satz('Zugang zur Baustelle', text)];
        case 'sammelplatz': return [satz('Sammelplatz', text)];
        default: return [text];
    }
};

/**
 * Baut den Uebernahme-Entwurf — oder `null`, wenn es nichts zu uebernehmen gibt
 * (kein Handbuch, kein Kopfsatz, oder schon uebernommen).
 *
 * Ein Handbuch MIT Kopfsatz, aber ohne einen einzigen Wert, ergibt einen
 * Entwurf mit leeren Listen und nicht `null`: die Oberflaeche soll dann sagen
 * „das Handbuch hat noch keine Baustellenordnung", nicht „es gibt kein
 * Handbuch".
 */
export const uebernahmeAusHandbuch = (
    bundle: Pick<BauordnungBundle, 'ordnung' | 'regeln'>,
    handbuch: Pick<Handbuch, 'ordnung' | 'org_modell'> | null | undefined,
): HandbuchUebernahme | null => {
    const o = bundle.ordnung;
    if (!o || !handbuch) return null;
    if (istAusHandbuchUebernommen(o)) return null;

    const regeln: HandbuchRegelEntwurf[] = [];
    const uebersprungen: UebersprungeneZeile[] = [];
    const jeFeld: HandbuchUebernahme['jeFeld'] = [];

    // Vorhandene Wortlaute je Kapitel — nichts wird doppelt angelegt. Die
    // Menge waechst mit den eigenen Zeilen mit, damit auch zwei gleiche
    // Handbuchzeilen nur einmal ankommen.
    const vorhanden = new Map<string, Set<string>>();
    const naechsteSortierung = new Map<string, number>();
    for (const r of bundle.regeln || []) {
        const menge = vorhanden.get(r.kapitel) ?? new Set<string>();
        menge.add(schluessel(r.wortlaut));
        vorhanden.set(r.kapitel, menge);
        naechsteSortierung.set(r.kapitel, Math.max(naechsteSortierung.get(r.kapitel) ?? 0, (r.sortierung ?? 0) + 1));
    }

    for (const feld of HANDBUCH_FELDER) {
        const kapitel = HANDBUCH_FELD_KAPITEL[feld];
        let anzahl = 0;
        for (const wortlaut of zeilenDesFelds(handbuch.ordnung, feld)) {
            const menge = vorhanden.get(kapitel) ?? new Set<string>();
            if (menge.has(schluessel(wortlaut))) {
                uebersprungen.push({ feld, wortlaut, grund: `steht im Kapitel „${kapitel}" bereits` });
                continue;
            }
            menge.add(schluessel(wortlaut));
            vorhanden.set(kapitel, menge);
            const sortierung = naechsteSortierung.get(kapitel) ?? 0;
            naechsteSortierung.set(kapitel, sortierung + 1);
            regeln.push({ kapitel, wortlaut, quelle: 'handbuch', art: 'regel', sortierung, feld });
            anzahl += 1;
        }
        if (anzahl > 0) jeFeld.push({ feld, anzahl });
    }

    return {
        regeln,
        org_modell: orgModellAusHandbuch(handbuch.org_modell, o.org_modell),
        uebersprungen,
        jeFeld,
    };
};

/** Hat die Uebernahme ueberhaupt etwas zu schreiben? */
export const uebernahmeIstLeer = (u: HandbuchUebernahme | null): boolean =>
    !u || (u.regeln.length === 0 && u.org_modell === null);

/** Was der Entwurf an einer Regel-Zeile der Datenbank hinterlaesst — fuer den Dienst. */
export const alsRegelVorlage = (e: HandbuchRegelEntwurf): Pick<Regel, 'kapitel' | 'wortlaut' | 'quelle' | 'art' | 'sortierung'> => ({
    kapitel: e.kapitel,
    wortlaut: e.wortlaut,
    quelle: e.quelle,
    art: e.art,
    sortierung: e.sortierung,
});
